import React from "react";
import styled from "styled-components";
import { BASIC_WHITE } from "../../../constants/colors";
import { useAsset } from "../../../hooks/useAsset";
import { useStore } from "../../../hooks/useStore";
import { Button } from "../../atoms/Button";
import { CampaignSelection } from "../../organisms/campaign/CampaignSelection";
import { SearchStoreByAddressForm } from "../../organisms/form/SearchStoreByAddressForm";
import { SearchStoreByTextForm } from "../../organisms/form/SearchStoreByTextForm";
import { BannerSlideShow } from "../../organisms/home/BannerSlideShow";
import { HomeItem } from "../../organisms/home/HomeItem";
import { FavoriteStoreSelection } from "../../organisms/store/FavoriteStoreSelection";
import { useHome } from "./hook";

type Props = {
  showAppCouponHeader: boolean;
};

export const Home: React.FC<Props> = ({ showAppCouponHeader }) => {
  const {
    campaign,
    activeCampaigns,
    banners,
    handleSearchByAddress,
    handleSearchByText,
    handleSearchByLocation,
    handleSearchByMyStore,
    handleCampaignClick,
    handleRegularOrderClick,
    stockItemInfoMessage,
    isStockItemMode,
  } = useHome();

  const { allPrefectures, selectablePrefectures, favoriteStores } = useStore(
    [],
  );
  const { baseDir } = useAsset();

  // キャンペーンがある場合は選択画面を表示
  // ただし既にキャンペーンが選択済みの場合は店舗検索UIを表示（店舗変更フロー）
  if (activeCampaigns.length > 0 && !campaign) {
    return (
      <HomeContainer $hasAppCoupon={showAppCouponHeader}>
        {banners.length > 0 && <BannerSlideShow banners={banners} />}
        <CampaignSelection
          campaigns={activeCampaigns}
          handleRegularOrderClick={handleRegularOrderClick}
          handleCampaignClick={handleCampaignClick}
        />
      </HomeContainer>
    );
  }

  // キャンペーンがない場合は通常のホーム画面を表示
  return (
    <HomeContainer $hasAppCoupon={showAppCouponHeader}>
      {banners.length > 0 && <BannerSlideShow banners={banners} />}
      <HomeTitle>
        <Icon
          style={{
            backgroundImage:
              "url('" + baseDir + "/assets/images/home/home_icon@2x.png')",
          }}
          $backgroundColor={BASIC_WHITE}
        />
        お受け取り店舗を選択してご注文
      </HomeTitle>

      {isStockItemMode && stockItemInfoMessage && (
        <StockItemInfo>
          <StockItemInfoText>{stockItemInfoMessage}</StockItemInfoText>
        </StockItemInfo>
      )}

      {favoriteStores && favoriteStores.length > 0 && (
        <HomeItem
          title="マイ店舗から選択"
          iconUrl={baseDir + "/assets/images/home/mystore_icon@2x.png"}
          iconBackgroundColor={BASIC_WHITE}
        >
          <FavoriteStoreSelection
            favoriteStores={favoriteStores}
            onSelect={handleSearchByMyStore}
          />
        </HomeItem>
      )}

      {selectablePrefectures.length > 0 && (
        <HomeItem
          title="住所から選択"
          iconUrl={baseDir + "/assets/images/home/address_icon@2x.png"}
        >
          <SearchStoreByAddressForm
            allPrefectures={allPrefectures}
            selectablePrefectures={selectablePrefectures}
            handleSubmit={handleSearchByAddress}
          />
        </HomeItem>
      )}

      <HomeItem
        title="店舗名・住所を入力して検索"
        iconUrl={baseDir + "/assets/images/home/search_icon@2x.png"}
      >
        <SearchStoreByTextForm handleSubmit={handleSearchByText} />
      </HomeItem>

      <HomeItem
        title="現在地から選択"
        iconUrl={baseDir + "/assets/images/home/location_icon@2x.png"}
      >
        <ButtonContainer>
          <Button
            btnStyle="primary"
            onClick={handleSearchByLocation}
            text="現在地から店舗検索"
          />
        </ButtonContainer>
      </HomeItem>
    </HomeContainer>
  );
};

const HomeContainer = styled.div<{ $hasAppCoupon: boolean }>`
  padding: 0;
  margin: ${({ $hasAppCoupon }) =>
    $hasAppCoupon ? "100px auto 65px" : "0 auto 65px"};
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
`;

const HomeTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  font-weight: 600;
  margin: 16px 0 5px;
`;

const Icon = styled.div<{ $backgroundColor: string }>`
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  width: 20px;
  height: 20px;
  margin: 0 5px;
`;

const ButtonContainer = styled.div`
  button {
    height: auto;
    padding: 20px 0 18px;
    font-weight: 600;
    border-radius: 8px;
  }
`;

const StockItemInfo = styled.div`
  padding: 20px 20px;
  margin: 0 15px 10px 15px;
  border-radius: 8px;
  border: 1px solid #e66231;
  text-align: center;
`;

const StockItemInfoText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: #e66231;
`;
